import React from 'react';
import { Percent } from 'lucide-react';
import { StatusTag, CollapsibleCard, MetricTile } from '../../../common';
import { rateTone } from '../../../../utils/recordStatus';
import { formatNumber } from '../../../../utils/format';
import { getVisitRates } from './campaignDetail.controller';

/** Footer figure of the analysis card. */
const SummaryFigure = ({ label, value, toneClass = 'text-surface-900' }) => (
    <div className="text-center">
        <div className="text-[13px] text-surface-500">{label}</div>
        <div className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums ${toneClass}`}>{value}</div>
    </div>
);

function CampaignAnalytics({ analytics }) {
    const {
        totalStores,
        uniqueStoresVisited,
        totalVisits,
        availableCount,
        notAvailableCount,
        complianceRate,
        totalImages,
        pendingImages,
        availableVisitsCount
    } = analytics;

    const { visitRate, avgVisitsPerStore } = getVisitRates(analytics);

    return (
        <CollapsibleCard
            title="Análisis de Campaña"
            icon={<Percent />}
            iconTone="violet"
            defaultExpanded
            badges={<StatusTag tone="violet">{totalVisits} auditorías</StatusTag>}>
            {/* Main Stats */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-4">
                <MetricTile
                    label="Tasa de Cumplimiento"
                    value={complianceRate.toFixed(1)}
                    suffix="%"
                    tone={rateTone(complianceRate)}
                    progress={complianceRate}
                    hint={`${formatNumber(availableCount)} de ${formatNumber(uniqueStoresVisited)} pisos únicos cumplieron`}
                />
                <MetricTile
                    label="Cobertura de Pisos"
                    value={visitRate.toFixed(1)}
                    suffix="%"
                    tone={rateTone(visitRate)}
                    progress={visitRate}
                    hint={`${formatNumber(uniqueStoresVisited)} de ${formatNumber(totalStores)} pisos visitados`}
                />
                <MetricTile
                    label="Imágenes Procesadas"
                    value={formatNumber(totalImages)}
                    tone="success"
                    hint={pendingImages > 0 ? `${formatNumber(pendingImages)} pendientes` : 'Todas procesadas'}
                />
            </div>

            {/* Secondary Stats */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <MetricTile label="Total de Visitas" value={formatNumber(totalVisits)} hint="Auditorías realizadas" />
                <MetricTile label="Promedio Visitas/Piso" value={avgVisitsPerStore} hint="Frecuencia de auditoría" />
                <MetricTile
                    label="Material Disponible"
                    value={formatNumber(availableCount)}
                    tone="success"
                    hint={`${formatNumber(availableVisitsCount || 0)} visitas totales`}
                />
                <MetricTile
                    label="Material No Disponible"
                    value={formatNumber(notAvailableCount)}
                    tone="danger"
                    hint={`${formatNumber(totalVisits - (availableVisitsCount || 0))} visitas totales`}
                />
            </div>

            {/* Summary Footer */}
            <div className="mt-5 pt-5 border-t border-surface-100 grid grid-cols-3 gap-4">
                <SummaryFigure label="Pisos Asignados" value={formatNumber(totalStores)} />
                <SummaryFigure label="Pisos Únicos Visitados" value={formatNumber(uniqueStoresVisited)} toneClass="text-violet-fg" />
                <SummaryFigure label="Total Auditorías" value={formatNumber(totalVisits)} toneClass="text-primary-600" />
            </div>
        </CollapsibleCard>
    );
}

export default CampaignAnalytics;
